<template>
  <wizard
    first-step="personalDetails"
    class="wizard-deferral-of-payment"
  >
    <wizard-step
      :i18n="i18n"
      name="personalDetails"
      :model="formData"
      :next-step="submitPersonalDetails"
    >
      <wizard-step-intro>
        <h2>{{ i18n.wizardSteps.personalDetails.title }}</h2>
        <slot name="personal-details-paragraph" />
      </wizard-step-intro>

      <fieldset>
        <FormulateInput
          name="bonusCode"
          type="text"
          label="bonus code"
          validation="nohtml"
          class="custom-form-element"
          aria-hidden="true"
        />
        <FormulateInput
          name="website"
          type="text"
          label="website"
          validation="nohtml"
          class="custom-form-element"
          aria-hidden="true"
        />
        <FormulateInput
          :label="i18n.form.labels.name"
          name="name"
          validation="bail|required:trim|nohtml"
          :validation-messages="i18n.form.validations.name"
        />

        <FormulateInput
          name="email"
          type="email"
          :label="i18n.form.labels.email"
          validation="bail|required:trim|email"
          :validation-messages="i18n.form.validations.email"
        />

        <FormulateInput
          name="phoneNumber"
          type="text"
          :label="i18n.form.labels.phoneNumber"
          validation="bail|required:trim|nohtml"
          :validation-messages="i18n.form.validations.phoneNumber"
        />

        <FormulateInput
          type="text"
          name="debtorNumber"
          :label="i18n.form.labels.debtorNumber"
          :help="i18n.form.help.debtorNumber"
          validation="bail|required:trim|max:9,length|min:6,length|nohtml|matches:/^[0-9\-]+$/"
          validation-name="Klantnummer"
          :validation-messages="i18n.form.validations.debtorNumber"
          @keydown.space.prevent
        />
      </fieldset>
    </wizard-step>

    <wizard-step
      :i18n="i18n"
      name="deferralOfPayment"
      :model="formData"
      next-step="review"
      previous-step="personalDetails"
    >
      <wizard-step-intro>
        <h2>{{ i18n.wizardSteps.deferralOfPayment.title }}</h2>
        <slot name="deferral-of-payment-paragraph" />
      </wizard-step-intro>

      <fieldset>
        <h3>{{ i18n.form.headings.invoiceNumbers }}</h3>

        <FormulateInput
          type="group"
          name="invoices"
          :help="i18n.form.help.invoices"
          :add-label="i18n.form.addlabel.invoice"
          :repeatable="true"
          validation="bail|min:1,length|max:3,length|unique:invoiceNumber"
          :validation-messages="i18n.form.validations.invoices"
        >
          <div class="row">
            <!-- workaround for losing focus on first input -->
            <FormulateInput
              type="hidden"
              value="none"
            />
            <!-- /workaround -->
            <FormulateInput
              type="text"
              name="invoiceNumber"
              :label="i18n.form.labels.invoiceNumber"
              validation="bail|required:trim|number|min:6,length|max:10,length"
              :validation-messages="invoiceValidation"
            />
          </div>
        </FormulateInput>

        <div v-if="!forUsersWithNoChildBenefits">
          <h3>{{ i18n.form.headings.payments }}</h3>
          <fields-date-picker
            :i18n="i18n"
            :min-date="minDateForDeferralOfPayment"
            :max-date="maxDateForDeferralOfPayment"
            :date-validation="
              'bail|required|before:' +
                dateAfterMaxDateForDeferralOfPayment +
                '|after:' +
                dateBeforeMinDateForDeferralOfPayment
            "
          />
        </div>
      </fieldset>
    </wizard-step>

    <wizard-step
      :i18n="i18n"
      name="review"
      :model="formData"
      :next-step="sendRequest"
      previous-step="deferralOfPayment"
      :submit-button-text="i18n.form.buttons.submit"
      :is-submitting="request.isSending"
      :error-submitting="request.errorSending"
    >
      <wizard-step-intro>
        <h2>{{ i18n.wizardSteps.review.title }}</h2>
        <slot name="review-paragraph" />
      </wizard-step-intro>

      <table class="wizard-summary">
        <tbody>
          <tr>
            <th>{{ i18n.form.labels.reviewName }}</th>
            <td>{{ resultingData.personalDetails.name }}</td>
          </tr>
          <tr>
            <th>{{ i18n.form.labels.reviewEmail }}</th>
            <td>{{ resultingData.personalDetails.email }}</td>
          </tr>
          <tr>
            <th>{{ i18n.form.labels.reviewPhoneNumber }}</th>
            <td>{{ resultingData.personalDetails.phoneNumber }}</td>
          </tr>
          <tr>
            <th>{{ i18n.form.labels.reviewCustomerNumber }}</th>
            <td>{{ resultingData.personalDetails.debtorNumber }}</td>
          </tr>
          <tr
            v-for="(invoice, index) in resultingData.paymentDeferral.invoices"
            :key="index"
          >
            <td>
              <span v-if="!index">{{ i18n.form.labels.reviewInvoiceNumbers }}</span>
              <span v-else>&nbsp;</span>
            </td>
            <td>{{ invoice.invoiceNumber }}</td>
          </tr>
          <tr>
            <th>{{ i18n.form.labels.reviewPaymentDate }}</th>
            <td>{{ moment(resultingData.paymentDeferral.date).format('DD-MM-YYYY') }}</td>
          </tr>
        </tbody>
      </table>
    </wizard-step>
  </wizard>
</template>

<script>
  import FieldsDatePicker from '@/components/forms/fields/FieldsDatePicker.vue';
  import Wizard from '@/components/wizards/components/Wizard.vue';
  import WizardStep from '@/components/wizards/components/WizardStep.vue';
  import WizardStepIntro from '@/components/wizards/components/WizardStepIntro.vue';
  import utils from '@/lib/utils';
  import axios from 'axios';
  import moment from 'moment';
  import getQueryParams from '@/lib/formulate/getFormQueryParam';

  export default {
    name: 'WizardDeferralOfPayment',
    components: {
      FieldsDatePicker,
      Wizard,
      WizardStep,
      WizardStepIntro,
    },
    props: {
      i18n: {
        type: Object,
        default: null,
      },
      prefillData: {
        type: Object,
        default: () => { },
      },
      locale: {
        type: String,
        default: () => 'nl',
      },
      label: {
        type: String,
        default: 'Partou',
      },
      forUsersWithNoChildBenefits: {
        type: Boolean,
        default: () => false,
      },
      minDateForDeferralOfPayment: {
        type: String,
        default: () => null,
      },
      maxDateForDeferralOfPayment: {
        type: String,
        default: () => null,
      },
      successPageUrl: {
        type: String,
        default: () => '/',
      },
      formToken: {
        type: String,
        default: () => '',
      },
    },
    data() {
      return {
        formData: {
          personalDetails: this.$store.state.wizard.personalDetails,
          deferralOfPayment: {},
        },
        request: {
          isSending: false,
          errorSending: false,
        },
      };
    },
    computed: {
      moment() {
        return moment;
      },
      dateBeforeMinDateForDeferralOfPayment() {
        return utils.date.getDate(new Date(this.minDateForDeferralOfPayment));
      },
      dateAfterMaxDateForDeferralOfPayment() {
        return utils.date.getDate(new Date(this.maxDateForDeferralOfPayment), 1);
      },
      resultingData() {
        return {
          label: this.label,
          locale: this.locale,
          formToken: this.formToken,
          personalDetails: this.formData.personalDetails,
          paymentDeferral: {
            ...this.formData.deferralOfPayment
          },
        };
      },
      invoiceValidation() {
        const { max, required, number } = this.i18n.form.validations.invoiceNumber
        return {
          min: max,
          max,
          number,
          required
        }
      }
    },
    created() {
      this.formData.personalDetails = {
        ...this.formData.personalDetails,
        ...getQueryParams(this.prefillData)
      }
    },
    methods: {
      submitPersonalDetails() {
        this.$store.dispatch('wizard/setPersonalDetails', this.formData.personalDetails);
        return 'deferralOfPayment';
      },
      sendRequest() {
        this.request.errorSending = false;
        this.request.isSending = true;
        const requestVerificationToken = document.body.querySelector('input[type=hidden][name=__RequestVerificationToken]').value;

        const config = {
          headers: {
            'X-REQUEST-VERIFICATION-TOKEN': requestVerificationToken,
          }
        };

        axios
          .post('/umbraco/api/wizard/paymentdeferral', this.resultingData, config)
          .then(() => {
            window.location.href = this.successPageUrl;
          })
          .catch(() => {
            this.request.isSending = false;
            this.request.errorSending = true;
          });
      },
    },
  };
</script>
